import { useState, useContext } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Row, Col } from 'reactstrap';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { CampaignContext } from '~/context/campaign';
import {
    getFundDisbursementGuidelinesUrl,
    getCampaignTypeFromPatient,
} from '../fundDisbursementGuidelines';

const S3_BASE = 'https://s3.amazonaws.com/helphopelive/expense-examples';

const CATEGORY_DOCS = {
    medical: {
        title: 'Medical',
        description:
            'Hospital, doctors, and lab fees not covered by insurance, including deductibles and copays; phone and TV rentals during hospitalization. Treatments that are FDA-approved or currently under FDA review and recommended by a medical professional. Disposable medical equipment and supplies for the client, including catheters, pads, briefs, and medically necessary OTC drugstore items.',
        requiredDocs:
            'Current unpaid medical bill or receipts showing products purchased',
        images: [
            `${S3_BASE}/medical-bill-1.png`,
            `${S3_BASE}/medical-bill-2.png`,
            `${S3_BASE}/medical-supplies.png`,
            `${S3_BASE}/medical-equipment.png`,
        ],
    },
    medications: {
        title: 'Medications',
        description:
            'Illness or Injury-related prescriptions, over-the-counter medications, and medication co-pays.',
        requiredDocs: 'Pharmacy bill or receipt',
        images: [`${S3_BASE}/medications.png`],
    },
    insurance: {
        title: 'Insurance',
        description:
            'Health insurance premiums, including dental and vision, for the client only. COBRA premiums for parents who must take a leave of absence or family medical leave to be a caregiver for a minor client. Leave documentation or FMLA is required. Cannot also request payment for full-time caregiver services.',
        requiredDocs:
            'Insurance bill showing policy and coverage. If on Social Security, a copy of the social security letter',
        images: [`${S3_BASE}/insurance.png`],
    },
    travel_mileage: {
        title: 'Travel/Mileage',
        description:
            'Local travel (one-day round trip) and long-distance travel (overnight or longer) for client and one caregiver to access treatment or for caregivers to visit the hospitalized client. Reasonable maintenance costs for a vehicle used for accessing treatments. Airfare, ride-shares, car rental, buses for treatments and appointments.',
        requiredDocs:
            'Map of travel route. Bill or receipt from mechanic showing the maintenance. Receipt from all other forms of transportation.',
        images: [`${S3_BASE}/travel-mileage.png`],
    },
    relocation_lodging: {
        title: 'Relocation/Lodging',
        description:
            "Housing and moving expenses for the client and one caregiver to move to access treatment. Necessary living expenses, such as mortgage payments (P&I only), rent, and utilities.",
        requiredDocs:
            "Relocation requires a doctor's note or dates of medical appointments, hotel receipt or bill. Living expenses require a doctor's note saying the primary wage earner is unable to work due to the client's medical condition, or transplant date, a copy of the lease if renting or a mortgage statement showing the breakdown of monthly principal and interest, and utility bills or receipts.",
        images: [`${S3_BASE}/relocation-lodging.png`],
    },
    food_incidentals: {
        title: 'Food/Incidentals',
        description:
            'Per diem for meals, drinks, and other minor expenses incurred during medical travel or temporary relocation for treatment. For the client and caregiver while the client is outpatient, and for the caregiver only while the client is in-patient.',
        requiredDocs: "Doctor's note or proof of medical appointments",
        images: [`${S3_BASE}/food-incidentals.png`],
    },
    physical_therapy: {
        title: 'Physical Therapy',
        description:
            'Medically recommended therapies including physical, occupational, activity-based, speech, and massage; therapeutic recreational camps; adaptive sports.',
        requiredDocs: 'Bill or receipt from provider',
        images: [`${S3_BASE}/physical-therapy.png`],
    },
    caregiving: {
        title: 'Caregiving',
        description:
            'One caregiver for any of the following: in-home nursing care, personal care assistants, travel aides, and hospital bedside caregivers. Help Hope Live does not cover childcare expenses.',
        requiredDocs:
            'Receipt showing payments made to caregivers and time sheets for worked hours. Bill from caregiving provider agency.',
        images: [`${S3_BASE}/caregiving-1.png`, `${S3_BASE}/caregiving-2.png`],
    },
    fundraising: {
        title: 'Fundraising',
        description: 'Reasonably priced materials.',
        requiredDocs: 'Receipts and/or bills from vendors or suppliers',
        images: [
            `${S3_BASE}/fundraising-1.png`,
            `${S3_BASE}/fundraising-2.png`,
        ],
    },
    other: {
        title: 'Other',
        description: '',
        requiredDocs:
            "Bill of sale or receipt. If it's for something not typically used medically, we need a doctor's note saying it's medically necessary for the client.",
    },
};

const DocumentInfoModal = ({ isOpen, toggle, categoryValue }) => {
    const [imageIndex, setImageIndex] = useState(0);
    const [{ campaign }] = useContext(CampaignContext);
    const info = CATEGORY_DOCS[categoryValue];
    const images = info?.images;

    if (!info) return null;

    const hasImages = images && images.length > 0;

    const handlePrev = () => {
        setImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    };

    const handleNext = () => {
        setImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl">
            <ModalHeader toggle={toggle}>
                <h3 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
                    Example Supporting Documents
                </h3>
            </ModalHeader>
            <ModalBody>
                <Row>
                    <Col xs={12} md={hasImages ? 5 : 12}>
                        <p>
                            <strong>{info.title}</strong> &ndash; {info.description}
                        </p>
                        <p>
                            <strong>Required Documentation:</strong> {info.requiredDocs}
                        </p>
                        <p style={{ marginTop: '20px' }}>
                            You can get a more detailed breakdown with examples by
                            checking out the{' '}
                            <a
                                href={getFundDisbursementGuidelinesUrl(
                                    getCampaignTypeFromPatient(campaign?.patient)
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#17a2b8', textDecoration: 'underline' }}
                            >
                                Fund Disbursement Guidelines
                            </a>
                            .
                        </p>
                    </Col>
                    {hasImages && (
                        <Col xs={12} md={7}>
                            <div style={{ position: 'relative' }}>
                                <img
                                    src={images[imageIndex]}
                                    alt={`Example document ${imageIndex + 1}`}
                                    style={{
                                        width: '100%',
                                        borderRadius: '8px',
                                        objectFit: 'contain',
                                    }}
                                />
                            </div>
                            {images.length > 1 && (
                                <div
                                    className="d-flex justify-content-end gap-2 mt-2"
                                >
                                    <button
                                        type="button"
                                        onClick={handlePrev}
                                        style={{
                                            width: '36px',
                                            height: '36px',
                                            borderRadius: '50%',
                                            border: '1px solid #333',
                                            background: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: 0,
                                        }}
                                        aria-label="Previous image"
                                    >
                                        <ChevronLeftIcon fontSize="small" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleNext}
                                        style={{
                                            width: '36px',
                                            height: '36px',
                                            borderRadius: '50%',
                                            border: '1px solid #333',
                                            background: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: 0,
                                        }}
                                        aria-label="Next image"
                                    >
                                        <ChevronRightIcon fontSize="small" />
                                    </button>
                                </div>
                            )}
                        </Col>
                    )}
                </Row>
            </ModalBody>
            <ModalFooter>
                <Button
                    color="dark"
                    outline
                    onClick={toggle}
                    style={{ borderRadius: '50px', padding: '10px 30px' }}
                >
                    CLOSE
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export const DocumentInfoButton = ({ categoryValue }) => {
    const [showModal, setShowModal] = useState(false);

    if (!categoryValue || !CATEGORY_DOCS[categoryValue]) return null;

    return (
        <>
            <div
                style={{
                    background: '#e8f6f8',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginTop: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                }}
                onClick={() => setShowModal(true)}
            >
                <InfoOutlinedIcon
                    style={{ color: '#17a2b8', marginRight: '8px', fontSize: '20px' }}
                />
                <span style={{ color: '#17a2b8', fontWeight: 600 }}>
                    What kind of documents do I need?
                </span>
            </div>
            <DocumentInfoModal
                isOpen={showModal}
                toggle={() => setShowModal(false)}
                categoryValue={categoryValue}
            />
        </>
    );
};

export default DocumentInfoModal;
